import { config } from '@/config';
import {
    IAccount,
    IAmazonGiftCard,
    ITransactionHistory,
    ITransactionRes,
    IWalletNonMemberData,
    IWalletPaymentRequestResponse,
    IWalletStartPaymentRequestData,
    IWalletStartPaymentResponse,
    IWalletPaymentDetailsRequestData,
    IWalletPaymentConfirmationResponse,
    IWalletPaymentConfirmationRequestData,
    IWalletNonMemberRequestData,
    IWalletPersonalData,
} from '@/types/types';
import { Config, makeFetcher } from '@/utils/fetch';
import { addQueryParamsToUrl, extractDataFromJson, getJson } from '@/utils/fp';

const makeWalletService = (apiUrl: string) => {
    const fetcher = makeFetcher(apiUrl);

    return {
        getAccounts: async (config?: Config) =>
            fetcher.get(`/accounts`, config).then(extractDataFromJson) as Promise<IAccount[]>,
        getTransactions: async (
            accountId: number,
            showArchivedTransactions: boolean,
            config: Config,
        ) =>
            fetcher
                .get(
                    `/account/${accountId}/transactions?showAll=${String(
                        showArchivedTransactions,
                    )}`,
                    config,
                )
                .then(getJson) as Promise<ITransactionRes>,
        getTransactionHistory: async (
            transactionId: number,
            languageId: number,
            token?: string,
            config?: Config,
        ): Promise<ITransactionHistory[]> => {
            const path = token
                ? `/non-member-payment/${token}/transaction/${transactionId}/history`
                : `/transaction/${transactionId}/history`;
            const pathWithParams = addQueryParamsToUrl(path)(`languageId=${languageId}`);
            return fetcher.get(pathWithParams, config).then(getJson) as Promise<
                ITransactionHistory[]
            >;
        },
        getParticipationInfo: async (key: string, config?: Config) =>
            fetcher.get(`/participation/${key}`, config).then(getJson) as Promise<{
                accountId: number;
                isArchivedTransaction: boolean;
            }>,
        cancelPayment: async (transactionId: number) =>
            fetcher.post(`/transaction/${transactionId}/cancel-payment`).then(getJson) as Promise<{
                messages: { message: string; phraseId: number }[];
            }>,
        amazonGiftCard: async (transactionId: number, config?: Config) =>
            fetcher
                .get(`/transaction/${transactionId}/amazon-gift-card`, config)
                .then(getJson) as Promise<IAmazonGiftCard>,
        getInvoiceDownloadLink: async (url: string, config?: Config) =>
            fetcher.get(url.replace('/wallet', ''), config).then(getJson) as Promise<{
                presignedInvoiceURL: string;
            }>,
        getPaymentRequestStartData: async (
            accountId: number,
            config?: Config,
        ): Promise<IWalletPaymentRequestResponse> =>
            fetcher
                .get(`/account/${accountId}/payment-request/start`, config)
                .then(getJson) as Promise<IWalletPaymentRequestResponse>,
        startPaymentRequest: async (
            accountId: number,
            data: IWalletStartPaymentRequestData,
        ): Promise<IWalletStartPaymentResponse> =>
            fetcher
                .post(`/account/${accountId}/payment-request/start`, JSON.stringify(data))
                .then(getJson) as Promise<IWalletStartPaymentResponse>,
        submitPaymentDetails: async (
            accountId: number,
            data: Partial<IWalletPaymentDetailsRequestData>,
        ): Promise<IWalletPaymentConfirmationResponse> =>
            fetcher
                .post(`/account/${accountId}/payment-request/details`, JSON.stringify(data))
                .then(getJson) as Promise<IWalletPaymentConfirmationResponse>,
        submitConfirmationDetails: async (
            accountId: number,
            data: Partial<IWalletPaymentConfirmationRequestData>,
        ) =>
            fetcher
                .post(`/account/${accountId}/payment-request/confirmation`, JSON.stringify(data))
                .then(getJson),

        // Non-member
        getNonMemberData: async (token: string, paymentMethodId?: number) =>
            fetcher
                .get(
                    `/non-member-payment/${token}${
                        paymentMethodId ? `?paymentMethodId=${paymentMethodId}` : ''
                    }`,
                    {
                        disableRedirects: true,
                    },
                )
                .then(getJson) as Promise<IWalletNonMemberData>,
        getNonMemberPersonalData: async (token: string, config?: Config) =>
            fetcher
                .get(`/non-member-payment/${token}/personal-details`, config)
                .then(getJson) as Promise<IWalletPersonalData>,
        submitNonMemberPayment: async (token: string, data: Partial<IWalletNonMemberRequestData>) =>
            fetcher.post(`/non-member-payment/${token}`, JSON.stringify(data)).then(getJson),
    };
};

export const walletService = makeWalletService(config.api.url.wallet);
